import { Observable } from 'rxjs';

/**
 * Identity emitted by a {@link AuthenticationService}. The backend looks
 * up {@link Users} rows either by the Auth0 `sub` claim (the historical
 * UPS path, used by every Auth0-first app) or by email (the new path
 * introduced for MSAL-authenticated apps such as Ryn's Roost).
 */
export type AuthenticatedIdentity =
  | { readonly kind: 'auth0'; readonly sub: string }
  | { readonly kind: 'email'; readonly email: string }
  | { readonly kind: 'id'; readonly id: string };

/**
 * Canonical, collision-free string key for an {@link AuthenticatedIdentity},
 * or `null` when there is no identity (or its discriminant carries no value).
 *
 * The single source of truth for identity-equality across the app: consumers
 * feed it to `distinctUntilChanged` so a token refresh that re-emits the same
 * user is quiet, and use it as a cache key / log token. Colocated with the
 * union deliberately — adding a variant to `AuthenticatedIdentity` forces a
 * new `case` HERE (the exhaustive switch has no default), and every consumer
 * that imports this function is then correct with no further edits. Do not
 * reintroduce per-app copies: they silently diverge and break on the next
 * variant (that is exactly what happened to the ryns-roost / ryn-replay /
 * ryn-control-center copies when the `'id'` variant landed).
 */
export function identityKey(
  identity: AuthenticatedIdentity | null
): string | null {
  if (!identity) return null;

  switch (identity.kind) {
    case 'auth0':
      return identity.sub ? `auth0:${identity.sub}` : null;
    case 'email':
      return identity.email ? `email:${identity.email.toLowerCase()}` : null;
    case 'id':
      return identity.id ? `id:${identity.id}` : null;
  }
}

export interface LogoutOption {
  returnTo?: string;
}

/**
 * Abstraction over how the current signed-in user is discovered. Apps
 * that rely on Auth0 (the default) don't need to provide this token —
 * {@link SecurityService} subscribes to Auth0's `idTokenClaims$` when no
 * source is injected.
 *
 * Apps using a different identity provider (e.g. MSAL/Azure AD) provide
 * this token in their app-level injector, typically in `app.config.ts`
 * via `{ provide: SECURITY_IDENTITY_SOURCE, useClass: MyMsalIdentitySource }`.
 *
 * The stream must emit a fresh identity on sign-in, the same identity on
 * token refresh (so `distinctUntilChanged` keeps things quiet), and
 * `null` on sign-out.
 *
 * Abstract class to not have to do injection token everywhere
 */
export abstract class AuthenticationService {
  abstract readonly identity$: Observable<AuthenticatedIdentity | null>;
  abstract readonly claims$: Observable<
    Record<string, unknown> | undefined | null
  >;
  abstract readonly isAuthenticated$: Observable<boolean>;

  /**
   * A token for authenticated API calls. `forceRefresh` (#31749) bypasses
   * the provider's token cache and forces a network refresh: MSAL
   * `acquireTokenSilent({ forceRefresh: true })`, Auth0
   * `getAccessTokenSilently({ ignoreCache: true })`. Default false keeps the
   * cache-first behavior every existing caller relies on byte-identical; it
   * exists so a 401-recovery path can replay with a genuinely new token
   * instead of the cached one that just 401'd.
   */
  abstract getAccessTokenSilently(forceRefresh?: boolean): Observable<string>;
  abstract logout(options: LogoutOption): void;
}
